import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import RecordPosition from "../RecordPosition/RecordPosition";
import { featuredProductHook } from "./useHook";

const FeaturedProductModal = ({ isOpen, onClose, productId }) => {
  const {
    getAllProducts,
    addFeaturedProduct,
    getRelation,
    deleteLinkedProduct,
    updateSimilarProductPosition,
  } = featuredProductHook();
  const [productsData, setProductsData] = useState([]);
  const [selectedFeaturedIds, setSelectedFeaturedIds] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [featuredProductsRelationsData, setFeaturedProductsRelationsData] =
    useState([]);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedForPosition, setSelectedForPosition] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      getAllProducts((data) => {
        setProductsData(data || []);
      }, productId);
      setSelectedFeaturedIds([]);
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    getRelation(productId, (data) => {
      setFeaturedProductsRelationsData(data || []);
    });
  }, [productId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleFeatured = (id) => {
    if (selectedFeaturedIds.includes(id)) {
      setSelectedFeaturedIds((prev) => prev.filter((pid) => pid !== id));
    } else {
      setSelectedFeaturedIds((prev) => [...prev, id]);
    }
  };

  const handleRemoveChip = (id) => {
    setSelectedFeaturedIds((prev) => prev.filter((pid) => pid !== id));
  };

  const handleSubmitFeatured = async () => {
    const payload = { productId, featuredIds: selectedFeaturedIds };
    await addFeaturedProduct(payload);
    Swal.fire({
      title: "Success",
      text: "Product added successfully",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      onClose();
    });
  };

  const handleDeleteLinkedProduct = async (id) => {
    await deleteLinkedProduct(id);
    Swal.fire({
      title: "Deleted",
      text: "Linked product has been deleted.",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      getRelation(productId, setFeaturedProductsRelationsData);
    });
  };

  const handlePositionClick = (relation, index) => {
    if (!relation.id) return;
    const currentPosition = relation.position ?? index + 1;
    setSelectedForPosition({ recordId: relation.id, currentPosition });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, position) => {
    await updateSimilarProductPosition(recordId, position);
    getRelation(productId, setFeaturedProductsRelationsData);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedForPosition(null);
  };

  const mainProduct =
    productsData && productsData.length > 0
      ? productsData.find((p) => p.id === productId)
      : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div className="bg-white p-4 rounded shadow-lg w-96">
        <h2 className="text-xl font-bold mb-4">Add/Delete Related Products</h2>

        {/* Linked Products Section */}
        <div className="mb-4">
          <h3 className="text-md font-bold">Related Products</h3>
          <ul className="mt-2">
            {Array.isArray(featuredProductsRelationsData) &&
            featuredProductsRelationsData.length > 0 ? (
              featuredProductsRelationsData.map((relation, index) => (
                <li
                  key={relation.similarProductId}
                  className="mb-2 flex justify-between items-center"
                >
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handlePositionClick(relation, index)}
                      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium min-w-[2rem]"
                    >
                      {relation.position ?? index + 1}
                    </button>
                    <span>
                      {relation.similarProductDetails?.name || "Unknown Product"}
                    </span>
                  </span>
                  <button
                    className="text-red-500 ml-2"
                    onClick={() => handleDeleteLinkedProduct(relation.id)}
                  >
                    &times;
                  </button>
                </li>
              ))
            ) : (
              <p>No related products available</p>
            )}
          </ul>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {selectedFeaturedIds.map((id) => {
            const product = productsData.find((p) => p.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700"
              >
                {product?.name || "Unknown"}
                <button
                  className="ml-2 text-blue-700 hover:text-blue-900"
                  onClick={() => handleRemoveChip(id)}
                >
                  &times;
                </button>
              </span>
            );
          })}
        </div>

        <div className="relative" ref={dropdownRef}>
          <button
            className="w-full border border-gray-300 bg-white px-4 py-2 rounded-md text-left focus:outline-none"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {selectedFeaturedIds.length
              ? "Add more products..."
              : "Select products..."}
          </button>

          {isDropdownOpen &&
            Array.isArray(productsData) &&
            productsData.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow max-h-48 overflow-y-auto">
                {productsData.map((prod) => (
                  <div
                    key={prod.id}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 flex items-center"
                    onClick={() => handleToggleFeatured(prod.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFeaturedIds.includes(prod.id)}
                      readOnly
                      className="mr-2"
                    />
                    <span>{prod.name || "Unknown Product"}</span>{" "}
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            className="bg-gray-400 text-white py-2 px-4 rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            onClick={handleSubmitFeatured}
          >
            Save
          </button>
        </div>
      </div>

      {isPositionModalOpen && selectedForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedForPosition.currentPosition}
          recordId={selectedForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Similar Product"
        />
      )}
    </div>
  );
};

export default FeaturedProductModal;
